import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCalendarAlt, FaEye, FaPlus } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton, CustomTextInput } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const ExamConfigurationList = () => {
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [paginationMeta, setPaginationMeta] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { translations } = useSelector((state) => state.language);

  // Fetch exam configuration list when dependencies change
  useEffect(() => {
    if (school_id && academic_year_id) {
      fetchExamConfigList();
    }
  }, [
    school_id,
    academic_year_id,
    pagination.pageIndex,
    pagination.pageSize,
    apiSearch,
  ]);

  // Fetch exam configuration list
  const fetchExamConfigList = async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
        limit: pagination.pageSize,
        page: pagination.pageIndex + 1, // Convert 0-based to 1-based
        search: apiSearch.trim() || "",
      };

      const response = await AxiosInstance.post(
        `/exam/examConfig/list`,
        payload,
      );

      if (response?.data?.status) {
        const examData = response.data.data || [];
        const transformedData = examData.map((item) => ({
          id: item.id,
          academic_id: item.academic_id,
          academic_year: item.academic_year,
          exam_type: item.exam_type,
          start_date: item.start_date
            ? dayjs(item.start_date).format("DD-MM-YYYY")
            : "-",
          end_date: item.end_date
            ? dayjs(item.end_date).format("DD-MM-YYYY")
            : "-",
          // keep the raw flag so the Status column can read it
          isActive: item.isActive,
        }));

        setData(transformedData);

        // Store pagination metadata from API response
        if (response?.data?.pagination) {
          setPaginationMeta(response.data.pagination);
        }
      } else {
        setData([]);
        setPaginationMeta({
          current_page: 1,
          per_page: pagination.pageSize,
          total: 0,
          total_pages: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching exam config list:", error);
      setData([]);
      setPaginationMeta({
        current_page: 1,
        per_page: pagination.pageSize,
        total: 0,
        total_pages: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/user/examCreate");
  };

  const handleView = (row) => {
    navigate("/user/examCreate", {
      state: {
        ID: row.id,
        data: row,
      },
    });
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: translations.labels?.serialNo || "S.No",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          // Calculate serial number based on current page
          const serialNumber =
            pagination.pageIndex * pagination.pageSize + row.index + 1;
          return serialNumber;
        },
      },
      {
        accessorKey: "academic_year",
        header: translations.labels?.academicYear || "Academic Year",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "exam_type",
        header: translations.labels?.examType || "Exam Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "start_date",
        header: translations.labels?.startDate || "Start Date",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "end_date",
        header: translations.labels?.endDate || "End Date",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "isActive",
        header: translations.labels?.status || "Status",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const isActive = Number(row.original.isActive) === 1;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "2px 12px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 600,
                color: isActive ? "#0f7b34" : "#b42318",
                backgroundColor: isActive ? "#e6f4ea" : "#fde8e6",
              }}
            >
              {isActive
                ? translations.labels?.active || "Active"
                : translations.labels?.inactive || "Inactive"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations, pagination.pageIndex, pagination.pageSize],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.examConfiguration || "Exam Configuration"}
        </h1>
      </div>

      {/* Filters and Add Button */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.academicYear || "Academic Year"}
              name="academicYear"
              type="text"
              value={academic_year}
              placeholder={
                translations.labels?.selectAcademicYear ||
                "Select Academic Year"
              }
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-8 px-2 col-md-6 col-sm-12">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleAdd}
              >
                <FaPlus style={{ marginRight: "8px" }} />
                {translations.labels?.add || "ADD"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={paginationMeta.total}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        placeholder={translations.labels.searchButton}
        enableSearch={false}
        enableFiltering={false}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />
    </div>
  );
};

export default ExamConfigurationList;
